import React, { useCallback, useEffect, useRef } from 'react';
import { useCookieConsent } from '@/providers/CookieConsentProvider';
import { useFeedbackCollectorParams } from '@/hooks/useFeedbackCollectorUrl';
import type { FeatureFeedbackCollectorProps } from '@/types/feedbackCollector';

declare global {
  interface Window {
    oc?: { renderUnloadedComponents?: () => void };
  }
}

const COMPONENT_NAME = 'evo-feature-feedback-collector';
const DEFAULT_VERSION = '1';

const loadedScripts = new Set<string>();
const pendingLoads = new Map<string, Promise<void>>();

function loadOcClient(registryBase: string): Promise<void> {
  const src = `${registryBase}/oc-client/client.js`;
  if (loadedScripts.has(src) || window.oc) {
    return Promise.resolve();
  }

  const pending = pendingLoads.get(src);
  if (pending) {
    return pending;
  }

  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      loadedScripts.add(src);
      pendingLoads.delete(src);
      resolve();
    };
    script.onerror = () => {
      pendingLoads.delete(src);
      reject(new Error(`Failed to load OC client from ${src}`));
    };
    document.head.appendChild(script);
  });

  pendingLoads.set(src, load);
  return load;
}

export const FeatureFeedbackCollector: React.FC<FeatureFeedbackCollectorProps> = ({
  feature,
  className,
  theme = 'light',
  dismissible = true,
  language = 'en-GB',
}) => {
  const params = useFeedbackCollectorParams(feature);
  const { isResolved, hasAnalyticsConsent } = useCookieConsent();
  const containerRef = useRef<HTMLDivElement>(null);

  const analyticsGranted = isResolved && hasAnalyticsConsent;

  const mountComponent = useCallback(async () => {
    const container = containerRef.current;
    if (!container || !params) return;

    try {
      await loadOcClient(params.mfeRegistryBase);
    } catch (error) {
      console.error('[FeatureFeedbackCollector]', error);
      return;
    }

    const queryString = new URLSearchParams({
      feature: params.feature,
      product: params.product ?? '',
      accountToken: params.accountToken,
      apiHost: params.apiHost,
      customerName: params.customerName,
      orgId: params.orgId,
      userId: params.userId,
      theme,
      dismissible: String(dismissible),
      reDisplayAfterDays: String(params.reDisplayAfterDays),
      language,
    });

    const version = params.mfeVersion ?? DEFAULT_VERSION;
    const href = `${params.mfeRegistryBase}/${COMPONENT_NAME}/${version}/?${queryString}`;

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute('href', href);

    container.innerHTML = '';
    container.appendChild(ocComponent);

    window.oc?.renderUnloadedComponents?.();
  }, [dismissible, language, params, theme]);

  useEffect(() => {
    if (!params || !analyticsGranted) return;

    void mountComponent();

    const container = containerRef.current;

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [analyticsGranted, mountComponent, params]);

  if (!params || !analyticsGranted) return null;

  return <div ref={containerRef} className={className} />;
};
